/** Defines the editor's undoable pixel operations and applies them to pixel buffers with plain pixel math. */
import {PixelBuffer, Point} from './imageEditing';
import {SelectionMask} from './selectionMask';

export enum EditOperationType {
   EraseSelection = 'eraseSelection',
   EraseStroke = 'eraseStroke',
   Adjust = 'adjust',
}

export enum AdjustmentKind {
   Brightness = 'brightness',
   Contrast = 'contrast',
}

/** Makes every selected pixel transparent. */
export interface EraseSelectionOperation {
   type: EditOperationType.EraseSelection;
   mask: SelectionMask;
}

/** Makes the pixels under a round brush stroke transparent, limited to the selection active when it was drawn. */
export interface EraseStrokeOperation {
   type: EditOperationType.EraseStroke;
   points: Point[];
   diameter: number;
   clip: SelectionMask | null;
}

/** Changes brightness or contrast inside `clip`, or across the whole image when it is null. */
export interface AdjustOperation {
   type: EditOperationType.Adjust;
   kind: AdjustmentKind;
   amount: number;
   clip: SelectionMask | null;
}

export type EditOperation = EraseSelectionOperation | EraseStrokeOperation | AdjustOperation;

/** Copies a pixel buffer so it can be edited without touching the source. */
export const clonePixels = (pixels: PixelBuffer): PixelBuffer => ({
   width: pixels.width,
   height: pixels.height,
   data: new Uint8ClampedArray(pixels.data)
});

/** Lists dab centres along a stroke, at most a quarter diameter (and at least 1 px) apart so it has no gaps. */
export const getStrokeDabs = (points: Point[], diameter: number): Point[] => {
   const spacing = Math.max(1, diameter / 4);
   return points.flatMap((point, index) => {
      if (index === 0) {
         return [point];
      }
      const previous = points[index - 1];
      const steps = Math.max(1, Math.ceil(Math.hypot(point.x - previous.x, point.y - previous.y) / spacing));
      return Array.from({length: steps}, (_, step) => {
         const progress = (step + 1) / steps;
         return {
            x: previous.x + (point.x - previous.x) * progress,
            y: previous.y + (point.y - previous.y) * progress
         };
      });
   });
};

/** Makes pixels whose centre lies within a dab's radius transparent, skipping pixels outside `clip`. */
export const eraseDabs = (pixels: PixelBuffer, dabs: Point[], diameter: number, clip: SelectionMask | null): void => {
   const radius = diameter / 2;
   const radiusSquared = radius * radius;

   dabs.forEach(dab => {
      const left = Math.max(0, Math.floor(dab.x - radius));
      const right = Math.min(pixels.width - 1, Math.ceil(dab.x + radius));
      const top = Math.max(0, Math.floor(dab.y - radius));
      const bottom = Math.min(pixels.height - 1, Math.ceil(dab.y + radius));

      for (let y = top; y <= bottom; y += 1) {
         const dy = y + 0.5 - dab.y;
         for (let x = left; x <= right; x += 1) {
            const dx = x + 0.5 - dab.x;
            const index = y * pixels.width + x;
            if (dx * dx + dy * dy <= radiusSquared && (!clip || clip.data[index])) {
               pixels.data[index * 4 + 3] = 0;
            }
         }
      }
   });
};

/** Makes every selected pixel transparent. */
const eraseMask = (pixels: PixelBuffer, mask: SelectionMask): void => {
   for (let index = 0; index < mask.data.length; index += 1) {
      if (mask.data[index]) {
         pixels.data[index * 4 + 3] = 0;
      }
   }
};

/** Precomputes the adjusted value of every channel level, matching CSS brightness()/contrast(). */
const createAdjustmentTable = (kind: AdjustmentKind, amount: number): Uint8ClampedArray => {
   // Uint8ClampedArray clamps to 0–255 and rounds on assignment.
   const table = new Uint8ClampedArray(256);
   for (let value = 0; value < 256; value += 1) {
      table[value] = kind === AdjustmentKind.Brightness
         ? value * (1 + amount)
         : (value - 127.5) * (1 + amount) + 127.5;
   }
   return table;
};

/** Applies a brightness or contrast change to the colour channels inside `clip`, leaving alpha untouched. */
const adjustPixels = (pixels: PixelBuffer, kind: AdjustmentKind, amount: number, clip: SelectionMask | null): void => {
   const table = createAdjustmentTable(kind, amount);
   const pixelCount = pixels.width * pixels.height;

   for (let index = 0; index < pixelCount; index += 1) {
      if (clip && !clip.data[index]) {
         continue;
      }
      const offset = index * 4;
      pixels.data[offset] = table[pixels.data[offset]];
      pixels.data[offset + 1] = table[pixels.data[offset + 1]];
      pixels.data[offset + 2] = table[pixels.data[offset + 2]];
   }
};

/** Applies one operation to the buffer in place. */
export const applyOperation = (pixels: PixelBuffer, operation: EditOperation): void => {
   switch (operation.type) {
      case EditOperationType.EraseSelection:
         eraseMask(pixels, operation.mask);
         break;
      case EditOperationType.EraseStroke:
         eraseDabs(pixels, getStrokeDabs(operation.points, operation.diameter), operation.diameter, operation.clip);
         break;
      case EditOperationType.Adjust:
         adjustPixels(pixels, operation.kind, operation.amount, operation.clip);
         break;
   }
};

/** Renders a list of operations onto a copy of the original pixels. */
export const applyOperations = (original: PixelBuffer, operations: EditOperation[]): PixelBuffer => {
   const pixels = clonePixels(original);
   operations.forEach(operation => applyOperation(pixels, operation));
   return pixels;
};
